<template>
  <div>
    <div class="d-flex align-items-center mb-1">
      <b-form-input
        v-model="unitsSearchQuery"
        class="mr-1"
        placeholder="Buscar por sucursal, REMS, SIRH, estado o municipio"
      />
      <b-button
        v-if="unitsSearchQuery"
        variant="flat-secondary"
        class="btn-icon"
        @click="unitsSearchQuery = ''"
      >
        <feather-icon icon="XIcon" size="16"/>
      </b-button>
    </div>

    <b-alert
      v-if="unitsSearchQuery && visibleRows.length === 0"
      show
      variant="warning"
      class="p-1 mb-1"
    >
      No se encontraron sucursales con ese criterio.
    </b-alert>

    <dynamic-scroller
      ref="refUnitsScroller"
      :items="visibleRows"
      :min-item-size="66"
      key-field="key"
      class="crisis-units-scroller"
      :class="{'slot': loadedSource !== 'view'}"
    >
      <template #default="{ item, active, index }">
        <dynamic-scroller-item
          :item="item"
          :active="active"
          :data-index="index"
          :size-dependencies="getRowSizeDependencies(item)"
          :watch-data="true"
        >
          <div :key="item.key" :class="getRowWrapperClass(item)">
            <b-card v-if="item.type === 'state'" no-body class="border mb-0">
              <b-card-header header-tag="header" class="p-0" role="tab">
                <b-button
                  block
                  variant="main-light"
                  class="text-left d-flex align-items-center justify-content-between overflow-auto flex-nowrap"
                  @click="toggleState(item.stateKey)"
                >
                  <div class="d-flex align-items-center mr-1">
                    <span class="font-weight-bold">
                      <feather-icon icon="MapPinIcon" size="16" class="mr-50"/>
                      {{ item.name }}
                      <b-badge variant="primary" pill class="ml-50">
                        {{ item.totalBranches }}
                      </b-badge>
                    </span>
                    <b-button
                      v-if="item.id && !isReadOnly"
                      variant="flat-danger"
                      size="sm"
                      class="btn-icon ml-50"
                      @click.stop="confirmDeleteState(item.id, item.name)"
                      v-b-tooltip.hover.left="'Eliminar Estado'"
                    >
                      <feather-icon icon="TrashIcon" size="16"/>
                    </b-button>
                  </div>
                  <feather-icon :icon="isStateExpanded(item.stateKey) ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16"/>
                </b-button>
              </b-card-header>
            </b-card>

            <b-card v-else-if="item.type === 'municipality'" no-body class="border mb-0 shadow-none">
              <b-card-header header-tag="header" class="p-0" role="tab">
                <b-button
                  block
                  variant="main-light"
                  class="text-left d-flex align-items-center justify-content-between py-2 overflow-auto flex-nowrap"
                  @click="toggleMunicipality(item)"
                >
                  <div class="d-flex align-items-center mr-1">
                    <span class="font-weight-bold d-flex align-items-center">
                      {{ item.name }}
                      <b-badge variant="info" pill class="ml-50">
                        {{ item.totalBranches }}
                      </b-badge>
                    </span>
                    <b-button
                      v-if="item.id && !isReadOnly"
                      variant="flat-danger"
                      size="sm"
                      class="btn-icon ml-50"
                      @click.stop="confirmDeleteMunicipality(item.id, item.name)"
                      v-b-tooltip.hover.left="'Eliminar Municipio'"
                    >
                      <feather-icon icon="TrashIcon" size="16"/>
                    </b-button>
                  </div>
                  <div class="d-flex align-items-center">
                    <b-button
                      v-if="showOptionsByStatus && !isReadOnly"
                      variant="outline-primary"
                      size="sm"
                      class="mr-50"
                      @click.stop="openModalShelters(item.data)"
                    >
                      Agregar albergue
                    </b-button>
                    <feather-icon :icon="isMunicipalityExpanded(item.municipalityKey) ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14"/>
                  </div>
                </b-button>
              </b-card-header>
            </b-card>

            <b-card v-else no-body class="border mb-0">
              <b-card-header header-tag="header" class="p-0" role="tab">
                <b-button
                  block
                  variant="white"
                  class="text-left d-flex align-items-center justify-content-between py-2"
                  @click="toggleBranch(item.branch)"
                >
                  <span class="d-flex align-items-center overflow-hidden">
                    <feather-icon icon="HomeIcon" size="16" class="mr-50"/>
                    <strong class="text-truncate">{{ item.branch.description }}</strong>
                    <small class="text-muted ml-1 text-nowrap">SIRH: {{ item.branch.sirh }}</small>
                  </span>
                  <div class="d-flex align-items-center">
                    <span
                      v-if="facilityLockText(item.branch)"
                      class="small text-muted mr-1 d-flex align-items-center"
                    >
                      <feather-icon icon="LockIcon" size="14" class="mr-25"/>
                      {{ facilityLockText(item.branch) }}
                    </span>
                    <b-button
                      variant="flat-primary"
                      size="sm"
                      class="btn-icon mr-1"
                      @click.stop="refreshBranchManually(item.branch)"
                      v-b-tooltip.hover.left="'Actualizar información'"
                    >
                      <feather-icon icon="RefreshCwIcon" size="16"/>
                    </b-button>
                    <b-button
                      v-if="!isReadOnly && !isDeleteButtonTemporarilyHidden(item.branch)"
                      variant="flat-danger"
                      size="sm"
                      class="btn-icon mr-1"
                      @click.stop="confirmDelete(item.branch)"
                    >
                      <feather-icon icon="TrashIcon" size="16"/>
                    </b-button>
                    <feather-icon :icon="isBranchActive(item.branch) ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16"/>
                  </div>
                </b-button>
              </b-card-header>

              <b-card-body v-if="isBranchActive(item.branch)" class="p-0">
                <div class="position-relative branch-detail">
                  <div
                    v-if="isBranchLoading(item.branch)"
                    class="custom-branch-overlay"
                  >
                    <div class="text-center pt-4">
                      <b-spinner variant="primary"/>
                      <div class="mt-1 text-primary font-weight-bold">
                        Cargando...
                      </div>
                    </div>
                  </div>

                  <b-alert
                    v-else-if="getDetailStatus(item.branch) === DETAIL_ERROR"
                    show
                    variant="danger"
                    class="m-2"
                  >
                    Error cargando detalle de sucursal.
                    <b-button
                      variant="flat-primary"
                      size="sm"
                      class="ml-50"
                      @click="refreshBranchFromServer(item.branch, true)"
                    >
                      Reintentar
                    </b-button>
                  </b-alert>

                  <template v-else>
                    <b-alert
                      v-if="isLockedByOther(item.branch)"
                      show
                      variant="warning"
                      class="m-2 p-1"
                    >
                      <feather-icon icon="LockIcon" size="16" class="mr-50"/>
                      {{ facilityLockText(item.branch) }}
                    </b-alert>
                    <hr class="my-0" />
                    <b-tabs
                      :key="`branch-tabs-${getBranchKey(item.branch)}`"
                      :value="getActiveBranchTab(item.branch)"
                      class="crisis-branch-tabs"
                      nav-class="crisis-branch-nav mb-0"
                      content-class="p-2 background-dark-tab"
                      fill
                      @input="setActiveBranchTab(item.branch, $event)"
                    >
                      <b-tab lazy>
                        <template #title>
                          <feather-icon icon="ListIcon" size="16"/>
                          Afectaciones
                        </template>
                        <AffectationsTab
                          v-if="getActiveBranchTab(item.branch) === 0"
                          :isReadOnly="isFacilityReadOnly(item.branch, isReadOnly || !showOptionsByStatus)"
                          :branch="item.branch"
                          :affectation="branchAffectations[getBranchKey(item.branch)]"
                          :affects-units="affectsUnits"
                          :loading="loading"
                          :show-options-by-status="showOptionsByStatus"
                          @save="saveAffectation"
                        />
                      </b-tab>
                      <b-tab v-if="!isAtm(item.branch)">
                        <template #title>
                          <feather-icon icon="UsersIcon" size="16"/>
                          Empleados
                        </template>
                        <EmployeesTab
                          v-if="getActiveBranchTab(item.branch) === 1"
                          :key="`employees-${getBranchKey(item.branch)}`"
                          :is-active="getActiveBranchTab(item.branch) === 1"
                          :isReadOnly="isFacilityReadOnly(item.branch, isReadOnly || !showOptionsByStatus)"
                          :branch="item.branch"
                          :shelters-by-municipality="sheltersByMunicipality"
                          :affects-users-rh="affectsUsersRh"
                          :operational-security-crisis="operationalSecurityCrisis"
                          :show-options-by-status="showOptionsByStatus"
                          :refresh-version="getBranchRefreshVersion(item.branch)"
                          @save-affectation="saveAffectationForEmployee"
                          @save-employee-success="$emit('build-data-from-response', $event, { mode: 'patch' })"
                          style="min-height: 500px"
                        />
                      </b-tab>
                    </b-tabs>
                  </template>
                </div>
              </b-card-body>
            </b-card>
          </div>
        </dynamic-scroller-item>
      </template>
    </dynamic-scroller>

    <template v-if="operationalSecurityCrisis.units.length && loadedSource === 'view'">
      <div v-if="canUserSendToReview" class="row justify-content-center mt-2">
        <div class="col-4">
          <b-form-group
            label="Resposanble de revisión"
            label-class="h5 font-weight-normal"
            label-for="user-selected"
          >
            <v-select
              input-id="user-selected"
              v-model="userSelected"
              :options="userOptions"
              :placeholder="$t('custom_forms.assigned_to')"
              :reduce="(user) => user.id"
              class="comment-box mb-1"
              :filter-by="filterUsers"
              :get-option-label="getUserLabel"
              :disabled="loading"
            >
              <template v-slot:option="option">
                {{ option.user }} - {{ option.full_name }}
              </template>
            </v-select>
            <b-button
              variant="warning"
              class="ml-auto"
              :disabled="!userSelected || loading"
              @click="sendToReview()"
            >
              {{ $t("custom_forms.send_to_review") }}
            </b-button>
          </b-form-group>
        </div>
      </div>
      <div v-if="showValidateButtons" class="row justify-content-center">
        <div class="col-4">
          <b-form-group
            :label="$t('custom_forms.review_comments')"
            label-class="h5 font-weight-normal"
            label-for="validation_comment"
          >
            <b-form-textarea
              id="validation_comment"
              v-model="validationComment"
              rows="3"
              max-rows="6"
            />
            <div v-if="errorValidationComment" class="text-danger">
              {{ errorValidationComment }}
            </div>
            <div class="d-flex mt-1">
              <b-button
                variant="success"
                class="mr-1"
                :disabled="loading"
                @click="approve()"
              >
                {{ $t("actions.approve") }}
              </b-button>
              <b-button
                variant="danger"
                :disabled="loading"
                @click="reject()"
              >
                {{ $t("actions.reject") }}
              </b-button>
            </div>
          </b-form-group>
        </div>
      </div>
      <div v-if="showReopenButtons" class="row justify-content-center mt-3">
        <div class="col-4">
          <b-form-group
            label="Resposanble de reasignación"
            label-class="h5 font-weight-normal"
            label-for="user-selected"
          >
            <v-select
              v-model="userSelectedReasigned"
              :options="userReopenedOptions"
              :placeholder="$t('custom_forms.assigned_to_reopen')"
              :reduce="(user) => user.id"
              label="full_name"
              class="comment-box"
            >
              <template v-slot:option="option">
                {{ option.user }} - {{ option.full_name }}
              </template>
            </v-select>
            <div v-if="errorsReopen" class="text-danger">
              {{ errorsReopen }}
            </div>
            <b-button
              variant="warning"
              class="ml-auto mt-1"
              :disabled="!userSelectedReasigned || loading"
              @click="reopen()"
            >
              Reasigar formulario de crisis
            </b-button>
          </b-form-group>
        </div>
      </div>
    </template>

    <shelters-modal
      :op-sec-crisis="operationalSecurityCrisis"
      :municipality-data="municipalityData"
      :show-modal-shelters="showModalShelters"
      @refresh-shelters="getShelters($event, true)"
      @update:isActive="showModalShelters = false"
    />
  </div>
</template>

<script>
import { computed, getCurrentInstance, onMounted, onUnmounted, ref, watch } from '@vue/composition-api';
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import axios from "@/libs/axios";
import { useToast } from "vue-toastification/composition";
import Swal from "sweetalert2";
import { EventBus } from "@/libs/event-bus";
import AffectationsTab from "./AffectationsTab.vue";
import EmployeesTab from './EmployeesTab.vue';
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import vSelect from "vue-select";
import SheltersModal from './SheltersModal.vue';
import { useCrisisFacilityDetails } from './composables/useCrisisFacilityDetails';
import { useCrisisRemoteLockRefresh } from './composables/lock-refresh/useCrisisRemoteLockRefresh';
import { useCrisisUnitRows } from './composables/unit-rows/useCrisisUnitRows';

export default {
  props: {
    formStatus: {
      type: Object
    },
    lastStatus: {
      type: Object
    },
    loadedSource: {
      type: String,
      default: 'view'
    },
    operationalSecurityCrisis: {
      type: Object
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    allowEditOnLockFailure: {
      type: Boolean,
      default: true
    },
    showOptionsByStatus: {
      type: Boolean,
      default: false
    },
    user: {
      type: Object
    },
    userCapabilities: {
      type: Object,
      default: () => ({})
    }
  },
  components: {
    AffectationsTab,
    DynamicScroller,
    DynamicScrollerItem,
    EmployeesTab,
    SheltersModal,
    ToastificationContent,
    vSelect
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy;
    const toast = useToast();

    const affectsUnits = ref([]);
    const affectsUsersRh = ref({});
    const branchAffectations = ref({});
    const errorsReopen = ref(null);
    const errorValidationComment = ref(null);
    const expandedMunicipalities = ref({});
    const expandedStates = ref({});
    const activeBranches = ref({});
    const activeBranchTabs = ref({});
    const deleteButtonHiddenBranches = ref({});
    const loading = ref(false);
    const refUnitsScroller = ref(null);
    const scrollerRefreshTick = ref(0);
    const userOptions = ref([]);
    const userReopenedOptions = ref([]);
    const userSelected = ref(null);
    const userSelectedReasigned = ref(null);
    const validationComment = ref(null);
    const sheltersByMunicipality = ref({});
    const showModalShelters = ref(false);
    const municipalityData = ref({});
    const deleteButtonVisibilityTimers = {};
    const DELETE_BUTTON_LOCK_HIDE_MS = 4000;

    const contentToast = (message, type, icon = "CheckIcon") => {
      toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon,
          variant: type,
        },
      });
    };

    const {
      DETAIL_ERROR,
      DETAIL_LOADED,
      DETAIL_LOADING,
      LOCK_BLOCKED,
      LOCK_ERROR,
      LOCK_LOCKING,
      LOCK_OWNED,
      acquireLock,
      applyRemoteLockAcquired,
      getBranchKey,
      getDetailStatus,
      getLock,
      isFacilityReadOnly,
      fetchFacilityDetails,
      releaseLock,
    } = useCrisisFacilityDetails({ axios, props, notify: contentToast });

    // ===== Start: Virtualized unit row data =====
    // Builds search state and flattened rows for the unit accordion scroller.
    const {
      hasUnitsSearch,
      unitKeys,
      unitsSearchQuery,
      visibleRows,
    } = useCrisisUnitRows({
      props,
      getBranchKey,
      isMunicipalityExpanded: municipalityKey => isMunicipalityExpanded(municipalityKey),
      isStateExpanded: stateKey => isStateExpanded(stateKey),
    });
    // ===== End: Virtualized unit row data =====

    onMounted(async () => {
      EventBus.on('crisis-status-verify', crisisVerifyStatus);

      await getAffectsUnits();
      await getAffectsUsersRh();
      await getValidators();
      await getUsersReopened();
    });

    onUnmounted(() => {
      if (EventBus.off) {
        EventBus.off('crisis-status-verify', crisisVerifyStatus);
      }

      Object.keys(deleteButtonVisibilityTimers).forEach((key) => {
        clearTimeout(deleteButtonVisibilityTimers[key]);
        delete deleteButtonVisibilityTimers[key];
      });
    });

    // ===== Start: Virtual scroller refresh control =====
    // Forces vue-virtual-scroller to recalculate dynamic accordion heights.
    const requestScrollerRefresh = (clearSizes = false) => {
      scrollerRefreshTick.value += 1;

      vm.$nextTick(() => {
        if (refUnitsScroller.value?.forceUpdate) {
          refUnitsScroller.value.forceUpdate(clearSizes);
        }
      });

      setTimeout(() => {
        // Dynamic rows need a second pass after async tab content settles.
        scrollerRefreshTick.value += 1;
        if (refUnitsScroller.value?.forceUpdate) {
          refUnitsScroller.value.forceUpdate(clearSizes);
        }
      }, 120);
    };
    // ===== End: Virtual scroller refresh control =====

    // ===== Start: Remote lock refresh flow =====
    // Refreshes facility data when another user releases or acquires a lock.
    const {
      getBranchRefreshVersion,
      refreshBranchFromServer,
    } = useCrisisRemoteLockRefresh({
      LOCK_BLOCKED,
      LOCK_OWNED,
      activeBranches,
      acquireLock,
      applyRemoteLockAcquired,
      emit,
      fetchFacilityDetails,
      getBranchKey,
      getLock,
      isBranchActive: branch => isBranchActive(branch),
      props,
      requestScrollerRefresh,
      vm,
    });
    // ===== End: Remote lock refresh flow =====

    const canUserSendToReopen = computed(() => {
      const conditionReopen = (props.formStatus.isRejected || props.formStatus.isApproved) && !props.formStatus.isRejected;
      const userCanReopen = vm.$can('reopen', 'CrisisForm');

      return conditionReopen && userCanReopen && (props.userCapabilities.isOwner || props.userCapabilities.supervisorProfile);
    });

    const canUserSendToReview = computed(() => {
      if (!props.lastStatus) {
        return true;
      }

      const conditionForForm = (props.formStatus.inProgress || props.formStatus.isRejected || props.formStatus.isReopen);
      const userCanSendToReview = vm.$can('send_to_review', 'CrisisForm');

      return conditionForForm && userCanSendToReview && (props.userCapabilities.isOwner || props.userCapabilities.supervisorProfile);
    });

    const showValidateButtons = computed(() => {
      const formCondition = props.formStatus.isReview;
      const userCanValidate = vm.$can('validate', 'CrisisForm');
      const userOwnerValidationForm = (props.lastStatus && props.lastStatus.id_user_validation === props.user.id);

      return (
        formCondition &&
        userCanValidate && userOwnerValidationForm &&
        (props.userCapabilities.isOwner || props.userCapabilities.supervisorProfile)
      );
    });

    const showReopenButtons = computed(() => {
      const formCondition = props.formStatus.isApproved;
      const userCanValidate = vm.$can('validate', 'CrisisForm');
      const userOwnerValidationForm = (props.lastStatus && props.lastStatus.id_user_validation === props.user.id);

      return (
        formCondition &&
        userCanValidate && userOwnerValidationForm &&
        (props.userCapabilities.isOwner || props.userCapabilities.supervisorProfile)
      );
    });

    watch(unitKeys, () => {
      Object.keys(activeBranches.value).forEach((key) => {
        const branch = props.operationalSecurityCrisis.units.find((unit) => getBranchKey(unit) === key);

        if (branch) {
          releaseLock(branch);
        }
      });

      expandedStates.value = {};
      expandedMunicipalities.value = {};
      activeBranches.value = {};
      activeBranchTabs.value = {};
      requestScrollerRefresh();
    });

    watch(unitsSearchQuery, () => {
      requestScrollerRefresh();
    });

    const showDeleteLockedMessage = (branch) => {
      const owner = getLockOwnerText(branch);
      const message = owner
        ? `No se puede eliminar la sucursal porque está siendo editada por ${owner}`
        : 'No se puede eliminar la sucursal porque está siendo editada por otro usuario';

      hideDeleteButtonTemporarily(branch);
      contentToast(message, 'warning', 'LockIcon');
    };

    const isDeleteButtonTemporarilyHidden = (branch) => (
      deleteButtonHiddenBranches.value[getBranchKey(branch)] === true
    );

    const hideDeleteButtonTemporarily = (branch) => {
      const key = getBranchKey(branch);

      if (deleteButtonVisibilityTimers[key]) {
        clearTimeout(deleteButtonVisibilityTimers[key]);
      }

      deleteButtonHiddenBranches.value = {
        ...deleteButtonHiddenBranches.value,
        [key]: true
      };

      requestScrollerRefresh();

      deleteButtonVisibilityTimers[key] = setTimeout(() => {
        const nextHiddenBranches = { ...deleteButtonHiddenBranches.value };

        delete nextHiddenBranches[key];
        delete deleteButtonVisibilityTimers[key];
        deleteButtonHiddenBranches.value = nextHiddenBranches;
        requestScrollerRefresh();
      }, DELETE_BUTTON_LOCK_HIDE_MS);
    };

    const canDeleteBranch = async (branch) => {
      if (props.isReadOnly) {
        return {
          canDelete: false,
          releaseAfterConfirmation: false
        };
      }

      const hadOwnedLock = getLock(branch).status === LOCK_OWNED;

      // The lock endpoint is the source of truth before destructive actions.
      await acquireLock(branch, true);

      if (isLockedByOther(branch)) {
        showDeleteLockedMessage(branch);
        return {
          canDelete: false,
          releaseAfterConfirmation: false
        };
      }

      if (getLock(branch).status !== LOCK_OWNED) {
        contentToast('No fue posible validar el bloqueo de la sucursal', 'warning', 'LockIcon');
        return {
          canDelete: false,
          releaseAfterConfirmation: false
        };
      }

      return {
        canDelete: true,
        releaseAfterConfirmation: !hadOwnedLock
      };
    };

    const confirmDelete = async (branch) => {
      const deleteValidation = await canDeleteBranch(branch);

      if (!deleteValidation.canDelete) {
        return;
      }

      const result = await Swal.fire({
        title: `¿Eliminar sucursal ${branch.description} del formulario de crisis?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Eliminar",
        cancelButtonText: "Cancelar",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      });

      if (!result.value) {
        if (deleteValidation.releaseAfterConfirmation) {
          await releaseLock(branch).catch(() => {});
        }
        return;
      }

      const params = { id_unit: branch.id_unit };

      try {
        const response = await axios.delete(`/v1/operational-security-crisis/${props.operationalSecurityCrisis.id}/unit/delete`, { params });

        contentToast("Sucursal eliminada", "success", "CheckIcon");
        emit('build-data-from-response', response.data.data);
        emit('refresh-units');
      } catch (error) {
        contentToast('Error', 'danger', 'AlertTriangleIcon');
      } finally {
        if (deleteValidation.releaseAfterConfirmation) {
          await releaseLock(branch).catch(() => {});
        }
      }
    };

    const confirmDeleteMunicipality = (municipalityId, municipalityName) => {
      if (!municipalityId) return;

      Swal.fire({
        title: `¿Eliminar el municipio ${municipalityName} y todas sus sucursales?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Eliminar",
        cancelButtonText: "Cancelar",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          const params = { id_municipality: municipalityId };

          axios.delete(`/v1/operational-security-crisis/${props.operationalSecurityCrisis.id}/municipality/delete`, { params })
            .then((response) => {
              contentToast("Municipio eliminado", "success", "CheckIcon");
              emit('build-data-from-response', response.data.data);
              emit('refresh-units');
            });
        }
      });
    };

    const confirmDeleteState = (stateId, stateName) => {
      if (!stateId) return;

      Swal.fire({
        title: `¿Eliminar el estado ${stateName} y todas sus sucursales?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Eliminar",
        cancelButtonText: "Cancelar",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          const params = { id_region: stateId };

          axios.delete(`/v1/operational-security-crisis/${props.operationalSecurityCrisis.id}/region/delete`, { params })
            .then((response) => {
              contentToast("Estado eliminado", "success", "CheckIcon");
              emit('build-data-from-response', response.data.data);
              emit('refresh-units');
            });
        }
      });
    };

    const crisisVerifyStatus = () => {
      const userIsCrisisOwner = (props.lastStatus) && (props.lastStatus.id_user_validation === props.user.id);

      if (!userIsCrisisOwner) {
        return;
      }

      if (props.formStatus.isRejected) {
        errorsReopen.value = 'Seleccione a resposable de reasignación';
      }
    };

    const getLockOwnerText = (branch) => {
      const { owner } = getLock(branch);

      return typeof owner === 'string'
        ? owner.trim()
        : owner?.name ?? owner?.full_name ?? owner?.user;
    };

    const facilityLockText = (branch) => {
      const lock = getLock(branch);
      const owner = getLockOwnerText(branch);

      if (lock.status === LOCK_LOCKING) {
        return 'Bloqueando edición...';
      }

      if (lock.status === LOCK_BLOCKED && owner) {
        return `Bloqueado por ${owner}`;
      }

      if (lock.status === LOCK_BLOCKED) {
        return 'Bloqueado por otro usuario';
      }

      if (lock.status === LOCK_ERROR) {
        if (lock.editable) {
          return 'Editando sin bloqueo';
        }

        return 'Edición bloqueada';
      }

      if (lock.status === LOCK_OWNED) {
        return 'Editando';
      }

      return '';
    };

    const filterUsers = (option, label, search) => {
      const text = search.toLowerCase();

      return (
        option.user?.toLowerCase().includes(text) ||
        option.full_name?.toLowerCase().includes(text)
      );
    };

    const getAffectsUnits = async () => {
      await axios.get('v1/affects-units')
        .then(response => {
          affectsUnits.value = response.data.data;
        })
        .catch(() => {
          affectsUnits.value = [];
        });
    };

    const getAffectsUsersRh = async () => {
      await axios.get('v1/affects-users-rh')
        .then(response => {
          affectsUsersRh.value = response.data.data;
        })
        .catch(() => {
          affectsUsersRh.value = [];
        });
    };

    const getRowSizeDependencies = (item) => {
      if (item.type !== 'facility') {
        return [
          item.key,
          String(isStateExpanded(item.stateKey ?? '')),
          String(isMunicipalityExpanded(item.municipalityKey ?? '')),
          scrollerRefreshTick.value,
        ];
      }

      const key = getBranchKey(item.branch);

      return [
        String(isBranchActive(item.branch)),
        getDetailStatus(item.branch),
        getLock(item.branch).status,
        String(getLock(item.branch).editable),
        activeBranchTabs.value[key] ?? 0,
        scrollerRefreshTick.value,
      ];
    };

    const getRowWrapperClass = (item) => ({
      'crisis-unit-row': true,
      'crisis-unit-row-state': item.type === 'state',
      'crisis-unit-row-municipality': item.type === 'municipality',
      'crisis-unit-row-facility': item.type === 'facility',
    });

    const getShelters = (municipalityId, refresh = false) => {
      if (sheltersByMunicipality.value[municipalityId] && !refresh) {
        return;
      }

      const params = {
        id_municipality: municipalityId,
        id_operational_security_crisis: props.operationalSecurityCrisis.id
      };

      axios.get('v1/shelters', { params }).then(response => {
        const { data } = response;

        sheltersByMunicipality.value = {
          ...sheltersByMunicipality.value,
          [municipalityId]: data.data
        };
      })
        .catch(() => {
          sheltersByMunicipality.value[municipalityId] = [];
        });
    };

    const getUserLabel = (option) => `${option.user} - ${option.full_name}`;

    const getActiveBranchTab = (branch) => activeBranchTabs.value[getBranchKey(branch)] ?? 0;

    const getValidators = async () => {
      await axios.get('v1/operational-security-crisis/user-validators')
        .then(response => {
          userOptions.value = response.data.data;
        })
        .catch((error) => {
          const errorMessage = error?.response?.data?.message ?? "Error cargando validadores";
          contentToast(errorMessage, "danger", "AlertTriangleIcon");
        });
    };

    const getUsersReopened = async () => {
      await axios.get('v1/operational-security-crisis/user-reopened')
        .then(response => {
          userReopenedOptions.value = response.data.data;
        })
        .catch((error) => {
          const errorMessage = error?.response?.data?.message ?? "Error cargando responsables de reasignación";
          contentToast(errorMessage, "danger", "AlertTriangleIcon");
        });
    };

    const isBranchActive = (branch) => Boolean(activeBranches.value[getBranchKey(branch)]);
    const isBranchLoading = (branch) => {
      const status = getDetailStatus(branch);
      return status !== DETAIL_LOADED && status !== DETAIL_ERROR;
    };
    const isLockedByOther = (branch) => getLock(branch).status === LOCK_BLOCKED;
    const isStateExpanded = (stateKey) => hasUnitsSearch.value || expandedStates.value[stateKey] === true;
    const isMunicipalityExpanded = (municipalityKey) => hasUnitsSearch.value || expandedMunicipalities.value[municipalityKey] === true;

    const openModalShelters = (munData) => {
      municipalityData.value = munData;
      showModalShelters.value = true;
    };

    const approve = () => {
      errorValidationComment.value = null;

      if (!validationComment.value) {
        errorValidationComment.value = 'Comentario requerido';
        return;
      }

      const payload = {
        id_operational_security_crisis: props.operationalSecurityCrisis.id,
        comments: validationComment.value
      };

      loading.value = true;

      axios.post('v1/operational-security-crisis/approve', payload)
        .then(response => {
          const { data } = response;
          contentToast(data.message, 'success', 'CheckIcon');
          emit('build-data-from-response', data.data);
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const reject = () => {
      errorValidationComment.value = null;

      if (!validationComment.value) {
        errorValidationComment.value = 'Comentario requerido';
        return;
      }

      const payload = {
        id_operational_security_crisis: props.operationalSecurityCrisis.id,
        comments: validationComment.value
      };

      loading.value = true;

      axios.post('v1/operational-security-crisis/reject', payload)
        .then(response => {
          const { data } = response;
          contentToast(data.message, 'success', 'CheckIcon');
          emit('build-data-from-response', data.data);
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const reopen = async () => {
      const payload = {
        id_operational_security_crisis: props.operationalSecurityCrisis.id,
        id_user_reopened: userSelectedReasigned.value
      };

      loading.value = true;

      await axios.post('v1/operational-security-crisis/reopen', payload)
        .then(response => {
          const { data } = response;

          errorsReopen.value = null;

          contentToast(data.message, 'success', 'CheckIcon');
          emit('build-data-from-response', data.data);
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const saveAffectation = (branch) => {
      // Save is blocked unless this user owns the lock or fallback editing is enabled.
      if (isFacilityReadOnly(branch, props.isReadOnly || !props.showOptionsByStatus)) {
        contentToast('La sucursal está bloqueada para edición', 'warning', 'LockIcon');
        return;
      }

      const operationalSecurityCrisisUnit = props.operationalSecurityCrisis.units.find(
        unit => (unit.id_unit === branch.id_unit)
      );

      if (!operationalSecurityCrisisUnit) {
        contentToast('No se encontró la sucursal en el formulario', 'danger', 'AlertTriangleIcon');
        return;
      }

      loading.value = true;

      const payload = {
        id_unit: branch.id_unit,
        id_operational_security_crisis: props.operationalSecurityCrisis.id,
        affects_units: operationalSecurityCrisisUnit.affectsUnits,
        operation_impact: operationalSecurityCrisisUnit.operation_impact,
        out_off_comunication: operationalSecurityCrisisUnit.out_off_comunication,
        damaged_cameras: operationalSecurityCrisisUnit.damaged_cameras,
        affected_amount: operationalSecurityCrisisUnit.affected_amount,
        blocked_roads: operationalSecurityCrisisUnit.blocked_roads,
        blocked_roads_comments: operationalSecurityCrisisUnit.blocked_roads_comments,
        at_risk: branch.at_risk,
        sirh_nearest_branch: operationalSecurityCrisisUnit.sirh_nearest_branch,
        has_emergency_generator: branch.has_emergency_generator,
        emergency_generator_capacity: branch.emergency_generator_capacity,
        emergency_generator_diesel_level: branch.emergency_generator_diesel_level
      };

      axios.post('/v1/operational-security-crisis/units-affects', payload)
        .then(response => {
          contentToast(response.data.message, 'success', 'CheckIcon');
          emit('build-data-from-response', response.data.data, { mode: 'patch' });
        })
        .catch(() => {
          contentToast('Error', 'danger', 'AlertTriangleIcon');
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const saveAffectationForEmployee = (branch, blockedRoads) => {
      const operationalSecurityCrisisUnitIndex = props.operationalSecurityCrisis.units.findIndex(
        unit => (unit.id_unit === branch.id_unit)
      );

      if (operationalSecurityCrisisUnitIndex < 0) {
        contentToast('No se encontró la sucursal en el formulario', 'danger', 'AlertTriangleIcon');
        return;
      }

      props.operationalSecurityCrisis.units[operationalSecurityCrisisUnitIndex].blocked_roads = blockedRoads;

      saveAffectation(branch);
    };

    const sendToReview = async () => {
      const payload = {
        id_operational_security_crisis: props.operationalSecurityCrisis.id,
        id_user_validation: userSelected.value
      };

      loading.value = true;

      await axios.post('v1/operational-security-crisis/validation', payload)
        .then(response => {
          const { data } = response;

          contentToast(data.message, 'success', 'CheckIcon');
          emit('build-data-from-response', data.data);
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const setActiveBranchTab = (branch, tabIndex) => {
      activeBranchTabs.value = {
        ...activeBranchTabs.value,
        [getBranchKey(branch)]: tabIndex,
      };
      requestScrollerRefresh();
    };

    const refreshBranchManually = async (branch) => {
      // Manual refresh keeps the current accordion state while patching latest data.
      await refreshBranchFromServer(branch, true, { keepOpen: isBranchActive(branch) });
    };

    const toggleBranch = async (branch) => {
      const key = getBranchKey(branch);
      const nextState = !activeBranches.value[key];

      activeBranches.value = { ...activeBranches.value, [key]: nextState };
      requestScrollerRefresh(true);

      if (!nextState) {
        // Closing a facility ends its edit session and releases the server lock.
        await releaseLock(branch);
        requestScrollerRefresh(true);
        return;
      }

      // Opening always refreshes facility details before rendering editable content.
      await acquireLock(branch, !props.isReadOnly && props.showOptionsByStatus);
      await refreshBranchFromServer(branch, true, { keepOpen: true });
      requestScrollerRefresh(true);
    };

    const toggleMunicipality = (item) => {
      const nextState = !isMunicipalityExpanded(item.municipalityKey);

      expandedMunicipalities.value = {
        ...expandedMunicipalities.value,
        [item.municipalityKey]: nextState,
      };
      requestScrollerRefresh();

      if (nextState && item.id) {
        getShelters(item.id);
      }
    };

    const toggleState = (stateKey) => {
      expandedStates.value = {
        ...expandedStates.value,
        [stateKey]: !isStateExpanded(stateKey),
      };
      requestScrollerRefresh();
    };

    const validateUnit = (unit) => {
      let hasError = false;
      const fieldsToValidate = ['operation_impact', 'out_off_comunication', 'affectsUnits'];

      fieldsToValidate.forEach(field => {
        const value = unit[field];
        if (value === null || (Array.isArray(value) && value.length === 0)) {
          unit.errors[field] = 'Campo requerido';
          hasError = true;
        } else {
          unit.errors[field] = null;
        }
      });

      return hasError;
    };

    const isAtm = (branch) => {
      const category = branch?.category?.name?.toLowerCase() || ''
      const keywords = ['atm', 'atms', 'cajero', 'cajeros']

      return keywords.some(keyword => category.includes(keyword))
    };

    return {
      DETAIL_ERROR,
      activeBranches,
      affectsUnits,
      affectsUsersRh,
      branchAffectations,
      canUserSendToReopen,
      canUserSendToReview,
      errorsReopen,
      errorValidationComment,
      facilityLockText,
      filterUsers,
      getActiveBranchTab,
      getBranchKey,
      getBranchRefreshVersion,
      getDetailStatus,
      getRowSizeDependencies,
      getRowWrapperClass,
      getShelters,
      getUserLabel,
      isBranchActive,
      isBranchLoading,
      isDeleteButtonTemporarilyHidden,
      isFacilityReadOnly,
      isLockedByOther,
      isMunicipalityExpanded,
      isStateExpanded,
      loading,
      municipalityData,
      openModalShelters,
      refreshBranchManually,
      refreshBranchFromServer,
      showModalShelters,
      showValidateButtons,
      showReopenButtons,
      sheltersByMunicipality,
      userOptions,
      userReopenedOptions,
      userSelected,
      userSelectedReasigned,
      unitsSearchQuery,
      validationComment,
      visibleRows,
      isAtm,

      approve,
      confirmDelete,
      confirmDeleteMunicipality,
      confirmDeleteState,
      reject,
      reopen,
      refUnitsScroller,
      requestScrollerRefresh,
      saveAffectation,
      saveAffectationForEmployee,
      sendToReview,
      setActiveBranchTab,
      toggleBranch,
      toggleMunicipality,
      toggleState,
      validateUnit,
    };
  }
};
</script>

<style lang="scss" scoped>
.crisis-units-scroller {
  height: min(70vh, 760px);
  overflow-y: auto;
  padding-right: 0.25rem;
  &.slot {
    height: min(50vh, 500px);
  }
}

:fullscreen {
  .crisis-units-scroller {
    height: calc(100vh - 90px);
  }
}

.crisis-unit-row {
  box-sizing: border-box;
  margin-bottom: 0;
  padding-bottom: 0.75rem;
}

.crisis-unit-row-municipality {
  padding-left: 1rem;
}

.crisis-unit-row-facility {
  padding-left: 2rem;
}

.crisis-unit-row ::v-deep .card {
  box-shadow: none !important;
}

.branch-detail {
  min-height: 220px;
}

.custom-branch-overlay {
  background-color: rgba(255, 255, 255, 1);
  border-radius: 0.25rem;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 10;
}
</style>
